import {
  hasAnyInvalidCombinationFailure,
  hasAnyMarketClosedFailure,
  hasAnyMarketSuspendedFailure,
} from "@ppb/tbd-store/helpers/sportsbook-betting";
import {
  type BettingState,
  INVALID_SGM_COMBINATIONS_RUNNER_FAILURE_CODES,
  RUNNER_FAILURE_CODES,
} from "@ppb/betslip-core";

import type { TranslationKey } from "../translations/keys";

import { i18n } from "./i18n";
import {
  hasAnyInvalidSGMCombinationFailure,
  hasAnySameMarketCombinationFailure,
} from "../components/connected-sportsbook-betslip-mapper";

export enum LegHintKind {
  MarketClosed = "MARKET_CLOSED",
  MarketSuspended = "MARKET_SUSPENDED",
  NotCombinable = "NOT_COMBINABLE",
  BetBuilderNotCombinable = "BET_BUILDER_NOT_COMBINABLE",
  InvalidSGMCombination = "INVALID_SGM_COMBINATION",
}

export type LegHint = {
  kind: LegHintKind;
  hintMessage: string;
};

const HINT_BY_KIND: Record<LegHintKind, { i18n: TranslationKey }> = {
  [LegHintKind.MarketClosed]: { i18n: "I18N.BETSLIP.SBK.MARKET_CLOSED" },
  [LegHintKind.MarketSuspended]: { i18n: "I18N.BETSLIP.SBK.MARKET_SUSPENDED" },
  [LegHintKind.NotCombinable]: { i18n: "I18N.BETSLIP.NOT_COMBINABLE_SELECTIONS_KEEP_ONE" },
  [LegHintKind.BetBuilderNotCombinable]: { i18n: "I18N.BETSLIP.NOT_COMBINABLE_SELECTIONS_KEEP_ONE" },
  [LegHintKind.InvalidSGMCombination]: { i18n: "I18N.BETSLIP.NOT_COMBINABLE_SELECTIONS" },
};

const buildHint = (kind: LegHintKind): LegHint => ({
  kind,
  hintMessage: i18n({ key: HINT_BY_KIND[kind].i18n }),
});

const hasAnySgmCombinationFailure = (runnerFailures: BettingState.ImplyRunnerFailure[]): boolean =>
  runnerFailures.some(
    ({ failureCode }) =>
      INVALID_SGM_COMBINATIONS_RUNNER_FAILURE_CODES.includes(failureCode) ||
      failureCode === RUNNER_FAILURE_CODES.NOT_ELIGIBLE_SGM_SELECTION,
  );

export const getAvailabilityLegHint = (runnerFailures: BettingState.ImplyRunnerFailure[]): LegHint | undefined => {
  if (hasAnyMarketClosedFailure(runnerFailures)) return buildHint(LegHintKind.MarketClosed);
  if (hasAnyMarketSuspendedFailure(runnerFailures)) return buildHint(LegHintKind.MarketSuspended);
  return undefined;
};

export const getLegHint = (runnerFailures: BettingState.ImplyRunnerFailure[]): LegHint | undefined => {
  const availabilityHint = getAvailabilityLegHint(runnerFailures);
  if (availabilityHint) return availabilityHint;
  if (hasAnyInvalidCombinationFailure(runnerFailures) || hasAnySgmCombinationFailure(runnerFailures))
    return buildHint(LegHintKind.NotCombinable);
  return undefined;
};

export const getBetBuilderLegHint = (runnerFailures: BettingState.ImplyRunnerFailure[]): LegHint | undefined => {
  const availabilityHint = getAvailabilityLegHint(runnerFailures);
  if (availabilityHint) return availabilityHint;
  if (hasAnySameMarketCombinationFailure(runnerFailures)) return buildHint(LegHintKind.BetBuilderNotCombinable);
  if (hasAnyInvalidSGMCombinationFailure(runnerFailures)) return buildHint(LegHintKind.InvalidSGMCombination);
  return undefined;
};
